import './MediaLibrary.scss';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Collection from '../collection/Collection';
import Pagination from '../pagination/Pagination';
import { playlistStore, searchStore, generalStore} from '../../store';
import SearchBar from '../searchBar/SearchBar';
import { useTranslation } from 'react-i18next';
import Loader from '../loader/Loader';
import CreatePlaylistModal from '../createPlaylistModal/CreatePlaylistModal';
import PlaylistsOverlay from '../playlistsOverlay/PlaylistsOverlay';
import Modal from '../modal/Modal';
import { type DistributedMedium } from '../../api/types/index';
import UploadeModal from '../uploadeModal/UploadeModal';
import Button from '../button/Button';

const MediaLibrary = () => {
    const { t } = useTranslation();
    const { setAlert, resetAlert, setSelectedMediaItems, selectedMediaItems, loading, gridStyle } = generalStore();
    const { getPlaylistsRequest } = playlistStore();
    const { doSearch, isSearching, search, totalPages, results, totalItems } = searchStore();

    const [selectedMedium, setSelectedMedium] = useState<any>([]);
    const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] =
        useState<boolean>(false);
    const [isFromSelection, setIsFromSelection] = useState<boolean>(false);
    const [isSingleSelection, setIsSingleSelection] = useState<boolean>(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
    const [isUploadAssetOpen, setIsUploadAssetOpen] = useState<boolean>(false);
    const [selectedSource, setSelectedSource]= useState('my_playlists');

    useEffect(() => {
        return () =>  setSelectedMediaItems([]);
    }, []);

    useEffect(() => {
        if (selectedMediaItems.length > 0) {
            setAlert({
                message: t('add_selected_to_playlist'),
                type: 'info',
                display: true,
                isAutoDisappearing: false,
                onClick: () => setIsOverlayOpen(true),
                onClose: () => setSelectedMediaItems([]),
                actionButtonText: t('add')
            });
        } else {
            resetAlert();
        }
    }, [selectedMediaItems]);

    const resetSelections = () => {
        if (isSingleSelection) {
            setSelectedMedium([]);
            setIsSingleSelection(false);
        } else {
            setSelectedMediaItems([]);
        }
    };

    const handleCloseCreatePlaylistModal = () => {
        resetSelections();
        setIsCreatePlaylistOpen(false);
    };

    const handleCloseOverlay = () => {
        resetSelections();
        setIsOverlayOpen(false);
    };

    const handleCloseAssetUpload = () => {
        setIsUploadAssetOpen(false);
    };

    const handleOpenAssetUpload = () => {
        setIsUploadAssetOpen(true);
    };

    const handleOverlayOpenSingleSelection = (item: any) => {
        setSelectedMedium([item]);
        setIsSingleSelection(true);
        setIsOverlayOpen(true);
        setSelectedMediaItems([]);
    };

    const handleOpenCreatePlaylistModal = () => {
        setIsCreatePlaylistOpen(true);
        setIsOverlayOpen(false);
        setIsFromSelection(true);
    };

    /**
     *
     * @param {Object<{payload: string, state: string}>} event
     */
    const handleSuccessfulPlaylistCreation = async event => {
        resetSelections();
        if (event?.state !== 'redirecting') {
            setIsCreatePlaylistOpen(false);
            await getPlaylistsRequest();
        }
    };

    const handlePaginationClick = pageNumber => {
        const { page, ...searchWithoutPage } = search;
        doSearch({
            ...searchWithoutPage,
            page: pageNumber
        });
    };

    const renderLibraryContent = () => {
        return (
            <div className="b-media-wrapper">
                {results.length > 0 && (
                    <Collection
                        name="media-wrapper"
                        type={gridStyle}
                        selectedItems={selectedMediaItems}
                        items={results}
                        onAddIconClick={handleOverlayOpenSingleSelection}
                    />
                )}
                {totalPages > 1 && (
                    <Pagination
                        currentPage={search.page}
                        totalPages={totalPages}
                        onClick={handlePaginationClick}
                    />
                )}
            </div>
        );
    };

    const classes = classNames({
        'b-media-library': true
    });

    let content;
    if (loading) {
        content = <Loader loading={loading} />;
    } else {
        content = renderLibraryContent();
    }

    return (
        <div className={classes}>
            <SearchBar openAssetUpload={handleOpenAssetUpload}/>
            {isSearching ? <Loader /> : content}
            {isCreatePlaylistOpen && (
                <CreatePlaylistModal
                    isOpen={isCreatePlaylistOpen}
                    onClose={handleCloseCreatePlaylistModal}
                    onSuccess={handleSuccessfulPlaylistCreation}
                    isFromSelection={isFromSelection}
                    selectedMediaItems={
                        isSingleSelection ? selectedMedium : selectedMediaItems
                    }
                />
            )}
            {isOverlayOpen && (
                <Modal
                    show
                    onClose={handleCloseOverlay}
                    className="overlay"
                    position="left"
                    name='add to playlist modal'
                >
                    <Modal.Title>
                        <h4>{t('add_selected_to_playlist')}</h4>
                        <div className="button-container">
                            <Button
                                clickHandler={() => setSelectedSource('my_playlists')}
                                text={t('my_playlists')}
                                secondaryButton={selectedSource !== 'my_playlists'}
                            />
                            <Button
                                clickHandler={() => setSelectedSource('market_playlists')}
                                text={t('market_playlists')}
                                secondaryButton={selectedSource !== 'market_playlists'}
                            />
                            <Button
                                clickHandler={() => handleOpenCreatePlaylistModal()}
                                extraClass="new-playlist"
                                text={t('new_playlist')}
                            />
                        </div>
                    </Modal.Title>
                    <Modal.Content>
                        {selectedSource === 'my_playlists' &&  <PlaylistsOverlay
                            selectedMediaItems={
                                isSingleSelection
                                    ? selectedMedium
                                    : selectedMediaItems
                            }
                        />}
                        {selectedSource === 'market_playlists' && <PlaylistsOverlay
                            market
                            selectedMediaItems={
                                isSingleSelection
                                    ? selectedMedium
                                    : selectedMediaItems
                            }
                        />}
                    </Modal.Content>
                </Modal>
            )}
            {isUploadAssetOpen && (
                <UploadeModal
                    onClose={handleCloseAssetUpload}
                />
            )}
        </div>
    );
};

export default MediaLibrary;
